import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  Bot,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  ClipboardList,
  Minus,
} from 'lucide-react'

import { fetchAgentActivity } from '../lib/agentActivity'
import { displayWorkSummary } from '../../supabase/functions/_shared/workSummary'
import { supabase, signOut } from '../lib/supabase'
import { useTeamAccess } from '../contexts/AuthContext'
import { toDateString, previousDateString } from '../lib/timezone'
import { AppSidebar } from '../components/AppSidebar'
import { TopBar } from '../components/TopBar'
import { DatePicker } from '../components/ui/date-picker'
import { SidebarInset, SidebarProvider } from '../components/ui/sidebar'
import { Badge } from '../components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar'
import { Skeleton } from '../components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'

// ============================================================================
// Page: /dashboard/team/:teamId/standups/summary
//
// Wired to real Supabase data (previously a static mockup — see git history).
// Decisions made 2026-09-05, same planning pass as TeamCheckins.tsx:
//
// - Stat cards are for the selected day only (matches the date picker and
//   the Check-ins page), not a weekly rollup — the mock's "124 check-ins,
//   92% more than last week" scale didn't fit a single workspace/day.
// - "Blockers detected" = standup_responses.questions.blockers non-empty
//   that day. Never surfaced as a stat anywhere in this app before.
// - "Team sentiment" reuses the existing positive/neutral/negative
//   convention from TeamDashboard.tsx (>0.2 / <-0.2 thresholds) — the
//   mock's value here was a copy-paste artifact (a time, not a sentiment).
// - "Delivered {time}" shows the one real, team-wide digest's posted_at
//   (standup_digest_logs — one row per team per day, not per pod) on every
//   pod card, or "Not yet delivered" if none exists for that day (true for
//   every day right now — the table has zero rows on prod so far).
// - The mock's "No critical issues" bullet is dropped — nothing in the
//   schema defines "critical".
// - Detailed links retain the selected pod and date. Connector reporting
//   uses the same authorized activity history as member/connector detail pages,
//   including general activity as well as completed-work check-ins.
// ============================================================================

interface TeamStandupSummaryProps {
  teamId: string
}

interface Member {
  id: string
  name: string | null
  email: string | null
  pod_id: string | null
  avatar_url: string | null
}

interface Pod {
  id: string
  name: string
  description: string | null
}

interface AgentInfo {
  id: string
  user_id: string
  name: string
  client_id: string | null
  client_name: string | null
}

interface StandupRow {
  user_id: string | null
  questions: { yesterday?: string; today?: string; blockers?: string } | null
  sentiment: number | null
  created_at: string
  date: string
}

interface CheckinRow {
  agent_id: string
  task: string | null
  created_at: string
  checkin_date: string
}

interface DigestLog {
  posted_at: string | null
  human_entry_count: number
  agent_checkin_count: number
  pod_id: string | null
}

type TrendDirection = 'up' | 'down' | 'flat'

interface StatCard {
  label: string
  icon: React.ReactNode
  value: string
  trendLabel: string
  trendDirection: TrendDirection
  upIsGood: boolean
}

function TrendIndicator({ direction, upIsGood }: { direction: TrendDirection; upIsGood: boolean }) {
  const isGood = direction === 'flat' ? true : direction === 'up' ? upIsGood : !upIsGood
  const colorClass = isGood ? 'text-success' : 'text-destructive'
  const Icon = direction === 'down' ? ArrowDown : direction === 'up' ? ArrowUp : Minus
  return <Icon className={`h-3 w-3 ${colorClass}`} />
}

function countDelta(today: number, yesterday: number): { direction: TrendDirection; label: string } {
  const delta = today - yesterday
  if (delta === 0) return { direction: 'flat', label: 'Same as yesterday' }
  const word = delta > 0 ? 'more' : 'fewer'
  return { direction: delta > 0 ? 'up' : 'down', label: `${Math.abs(delta)} ${word} than yesterday` }
}

function hasBlocker(questions: StandupRow['questions'] | undefined): boolean {
  return !!questions?.blockers && questions.blockers.trim().length > 0
}

// Same thresholds as TeamDashboard.tsx's getSentimentDisplay — reused so
// "positive/neutral/negative" means the same thing everywhere in the app.
function sentimentLabel(avg: number | null): { emoji: string; label: string; className: string } {
  if (avg === null) return { emoji: '—', label: 'No data yet', className: 'text-muted-foreground' }
  if (avg > 0.2) return { emoji: '🙂', label: 'Positive', className: 'text-success' }
  if (avg < -0.2) return { emoji: '🙁', label: 'Negative', className: 'text-destructive' }
  return { emoji: '😐', label: 'Neutral', className: 'text-muted-foreground' }
}

const TEAM_AVATAR_PALETTE = [
  'bg-orange-500',
  'bg-cyan-500',
  'bg-violet-600',
  'bg-emerald-600',
  'bg-pink-500',
  'bg-blue-500',
]

function teamPaletteFor(seed: string): string {
  let hash = 0
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  return TEAM_AVATAR_PALETTE[hash % TEAM_AVATAR_PALETTE.length]
}

const PERSON_AVATAR_PALETTE = [
  'bg-slate-200 text-slate-700',
  'bg-cyan-100 text-cyan-700',
  'bg-rose-100 text-rose-700',
  'bg-amber-100 text-amber-700',
  'bg-violet-100 text-violet-700',
  'bg-fuchsia-100 text-fuchsia-700',
]

function personPaletteFor(seed: string): string {
  let hash = 0
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  return PERSON_AVATAR_PALETTE[hash % PERSON_AVATAR_PALETTE.length]
}

function displayName(m: Pick<Member, 'name' | 'email'>): string {
  return m.name || m.email?.split('@')[0] || 'Unknown user'
}

interface PodCardData {
  pod: Pod
  peopleCount: number
  people: { id: string; name: string; avatarUrl: string | null; time: string; update: string }[]
  agentsCount: number
  agents: { id: string; name: string; time: string; update: string }[]
  summaryBullets: string[]
}

function TeamSummaryCard({
  data,
  deliveredLabel,
  defaultExpanded,
  onViewCheckins,
  onViewAgents,
}: {
  data: PodCardData
  deliveredLabel: string | null
  defaultExpanded: boolean
  onViewCheckins: () => void
  onViewAgents: () => void
}) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const { pod } = data

  return (
    <div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full min-h-[44px] items-center justify-between gap-3 p-4 text-left sm:p-5"
          aria-expanded={expanded}
        >
          <div className="flex min-w-0 items-center gap-3">
            {/* Pod/team avatar is rounded-square, people avatars stay circular
                (default) -- the shape itself distinguishes the two at a
                glance, per docs/UI_STYLE_GUIDE.md §9 and TeamAgents.tsx/
                TeamManagement.tsx. This page had drifted to circular. */}
            <Avatar className="h-10 w-10 shrink-0 rounded-md">
              <AvatarFallback
                className={`rounded-md text-sm font-semibold text-white ${teamPaletteFor(pod.id)}`}
              >
                {pod.name.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="font-semibold text-foreground">{pod.name}</div>
              <div className="truncate text-sm text-muted-foreground">
                {pod.description || 'No description yet'}
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {deliveredLabel ? (
              <Badge variant="success" className="hidden font-medium sm:inline-flex">
                {deliveredLabel}
              </Badge>
            ) : (
              <Badge variant="outline" className="hidden font-medium text-muted-foreground sm:inline-flex">
                Not yet delivered
              </Badge>
            )}
            {expanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </button>

        {expanded && (
          // Let both report columns shrink and wrap; stack before the sidebar
          // leaves too little room for three readable columns.
          <div className="grid min-w-0 grid-cols-1 divide-y divide-border border-t border-border xl:grid-cols-[minmax(12rem,0.85fr)_minmax(0,1.4fr)_minmax(0,1.4fr)] xl:divide-x xl:divide-y-0">
            <div className="min-w-0 p-4 sm:p-5">
              <h3 className="mb-3 text-sm font-semibold text-foreground">
                Summary ({data.summaryBullets.length})
              </h3>
              <ul className="space-y-2.5">
                {data.summaryBullets.map((bullet) => (
                  <li key={bullet} className="flex items-start gap-2 text-sm text-foreground">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="min-w-0 p-4 sm:p-5">
              <h3 className="mb-3 text-sm font-semibold text-foreground">
                People ({data.peopleCount})
              </h3>
              {data.people.length === 0 ? (
                <p className="text-sm text-muted-foreground">No check-ins for this day.</p>
              ) : (
                <ul className="space-y-3">
                  {data.people.map((person) => (
                    <li key={person.id} className="flex items-start gap-3">
                      <Avatar className="h-8 w-8 shrink-0">
                        {person.avatarUrl && <AvatarImage src={person.avatarUrl} alt="" />}
                        <AvatarFallback className={`text-xs font-semibold ${personPaletteFor(person.id)}`}>
                          {person.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="min-w-0 break-words font-medium text-foreground">{person.name}</span>
                          <span className="shrink-0 text-xs text-muted-foreground">{person.time}</span>
                        </div>
                        <p className="truncate text-sm leading-relaxed text-muted-foreground">{person.update}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                onClick={onViewCheckins}
                // #1F8DA5 per explicit request on this page -- noticeably
                // darker/more muted than the --primary token (a brighter
                // cyan, ~#2bc6e8), so this is a deliberate one-off, not a
                // token substitution (docs/UI_STYLE_GUIDE.md §7).
                className="mt-3 inline-flex min-h-[44px] items-center gap-1 text-sm font-medium text-[#1F8DA5] hover:underline "
              >
                View all check-ins
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="min-w-0 p-4 sm:p-5">
              <h3 className="mb-3 text-sm font-semibold text-foreground">
                Connectors ({data.agentsCount})
              </h3>
              {data.agents.length === 0 ? (
                <p className="text-sm text-muted-foreground">No connector activity for this day.</p>
              ) : (
                <ul className="space-y-3">
                  {data.agents.map((agent) => (
                    <li key={agent.id} className="flex items-start gap-3">
                      <Avatar className="h-8 w-8 shrink-0">
                        <AvatarFallback className={`text-xs font-semibold ${personPaletteFor(agent.id)}`}>
                          {agent.name.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="min-w-0 break-words font-medium text-foreground">{agent.name}</span>
                          <span className="shrink-0 text-xs text-muted-foreground">{agent.time}</span>
                        </div>
                        <p className="truncate text-sm leading-relaxed text-muted-foreground">{agent.update}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                onClick={onViewAgents}
                className="mt-3 inline-flex min-h-[44px] items-center gap-1 text-sm font-medium text-[#1F8DA5] hover:underline "
              >
                View all connector reports
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
    </div>
  )
}

const TeamStandupSummary: React.FC<TeamStandupSummaryProps> = ({ teamId }) => {
  const navigate = useNavigate()
  const { role } = useTeamAccess(teamId)
  const isManager = role === 'manager'
  const [teamName, setTeamName] = useState<string | undefined>(undefined)
  const [selectedDate, setSelectedDate] = useState<string>(toDateString(new Date()))
  const [selectedPodId, setSelectedPodId] = useState<string>('all')

  const [members, setMembers] = useState<Member[]>([])
  const [pods, setPods] = useState<Pod[]>([])
  const [agents, setAgents] = useState<AgentInfo[]>([])
  const [standupsToday, setStandupsToday] = useState<StandupRow[]>([])
  const [standupsYesterday, setStandupsYesterday] = useState<StandupRow[]>([])
  const [checkinsToday, setCheckinsToday] = useState<CheckinRow[]>([])
  const [checkinsYesterday, setCheckinsYesterday] = useState<CheckinRow[]>([])
  const [digestLogs, setDigestLogs] = useState<DigestLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const previousDate = previousDateString(selectedDate)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)

      const [teamResult, membersResult, podsResult, agentsResult] = await Promise.all([
        supabase.from('teams').select('name').eq('id', teamId).single(),
        (supabase.from as any)('users')
          .select('id, name, email, pod_id, avatar_url')
          .eq('team_id', teamId),
        (supabase.from as any)('pods').select('id, name, description').eq('team_id', teamId),
        (supabase.from as any)('agents_team_visible')
          .select('id, user_id, name, client_id, client_name')
          .eq('team_id', teamId),
      ])

      if (cancelled) return

      if (teamResult.error || membersResult.error || podsResult.error || agentsResult.error) {
        console.error(
          'Failed to load Summary page data:',
          teamResult.error ?? membersResult.error ?? podsResult.error ?? agentsResult.error
        )
        setError('Could not load the summary for this team.')
        setLoading(false)
        return
      }

      const agentRows = (agentsResult.data ?? []) as AgentInfo[]
      const agentIds = agentRows.map((a) => a.id)

      const [standupsResult, checkinsResult, digestResult] = await Promise.all([
        supabase
          .from('standup_responses')
          .select('user_id, questions, sentiment, created_at, date')
          .eq('team_id', teamId)
          .in('date', [selectedDate, previousDate]),
        Promise.all(agentIds.map(agentId => fetchAgentActivity(teamId, agentId, previousDate, selectedDate)))
          .then(rows => ({ data: rows.flat(), error: null as unknown }),
            (error: unknown) => ({ data: [], error })),
        // Not .maybeSingle(): standup_digest_logs has a pod_id column for a
        // future per-pod digest, and the old .maybeSingle() here would throw
        // the instant a second row for this team+date exists (e.g. one
        // team-wide row (pod_id null) plus a pod-specific one). Fetch every
        // row for the day and resolve per-pod vs. team-wide below instead.
        (supabase.from as any)('standup_digest_logs')
          .select('posted_at, human_entry_count, agent_checkin_count, pod_id')
          .eq('team_id', teamId)
          .eq('date', selectedDate),
      ])

      if (cancelled) return

      if (standupsResult.error || checkinsResult.error) {
        console.error(
          'Failed to load Summary page activity:',
          standupsResult.error ?? checkinsResult.error
        )
        setError('Could not load the summary for this team.')
        setLoading(false)
        return
      }

      const allStandups = (standupsResult.data ?? []) as unknown as StandupRow[]
      const allCheckins = (checkinsResult.data ?? []) as CheckinRow[]

      setTeamName(teamResult.data?.name ?? undefined)
      setMembers((membersResult.data ?? []) as Member[])
      setPods((podsResult.data ?? []) as Pod[])
      setAgents(agentRows)
      setStandupsToday(allStandups.filter((s) => s.date === selectedDate))
      setStandupsYesterday(allStandups.filter((s) => s.date === previousDate))
      setCheckinsToday(allCheckins.filter((c) => c.checkin_date === selectedDate))
      setCheckinsYesterday(allCheckins.filter((c) => c.checkin_date === previousDate))
      setDigestLogs((digestResult.data as DigestLog[]) ?? [])
      setLoading(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [teamId, selectedDate, previousDate])

  const goToDashboard = () => navigate(`/dashboard/team/${teamId}`)
  const handleLogout = async () => {
    await signOut()
    navigate('/auth/login')
  }

  const { statCards, podCards, scopedStandupsToday } = useMemo(() => {
    const membersByPod = new Map<string, Member[]>()
    for (const m of members) {
      if (!m.pod_id) continue
      const list = membersByPod.get(m.pod_id)
      if (list) list.push(m)
      else membersByPod.set(m.pod_id, [m])
    }
    const membersById = new Map(members.map((m) => [m.id, m]))

    const standupByUserId = new Map<string, StandupRow>()
    for (const s of standupsToday) if (s.user_id) standupByUserId.set(s.user_id, s)

    const checkinsByAgentId = new Map<string, CheckinRow[]>()
    for (const c of checkinsToday) {
      const list = checkinsByAgentId.get(c.agent_id)
      if (list) list.push(c)
      else checkinsByAgentId.set(c.agent_id, [c])
    }
    const checkinsByAgentIdYesterday = new Map<string, CheckinRow[]>()
    for (const c of checkinsYesterday) {
      const list = checkinsByAgentIdYesterday.get(c.agent_id)
      if (list) list.push(c)
      else checkinsByAgentIdYesterday.set(c.agent_id, [c])
    }

    const earliestCheckin = (list: CheckinRow[] | undefined): CheckinRow | null => {
      if (!list || list.length === 0) return null
      return [...list].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      )[0]
    }

    // ---- top stat cards (selected day only, scoped to the selected pod --
    //      previously these always summed the whole team even when a
    //      specific pod was chosen from the dropdown, which silently
    //      disagreed with the pod-filtered cards below it). ----
    const statScopeMemberIds =
      selectedPodId === 'all' ? null : new Set((membersByPod.get(selectedPodId) ?? []).map((m) => m.id))
    const statScopeAgentIds =
      selectedPodId === 'all'
        ? null
        : new Set(
            agents.filter((a) => membersById.get(a.user_id)?.pod_id === selectedPodId).map((a) => a.id)
          )

    const scopedStandupsToday = statScopeMemberIds
      ? standupsToday.filter((s) => s.user_id && statScopeMemberIds.has(s.user_id))
      : standupsToday
    const scopedStandupsYesterday = statScopeMemberIds
      ? standupsYesterday.filter((s) => s.user_id && statScopeMemberIds.has(s.user_id))
      : standupsYesterday
    const scopedCheckinsToday = statScopeAgentIds
      ? checkinsToday.filter((c) => statScopeAgentIds.has(c.agent_id))
      : checkinsToday
    const scopedCheckinsYesterday = statScopeAgentIds
      ? checkinsYesterday.filter((c) => statScopeAgentIds.has(c.agent_id))
      : checkinsYesterday

    const checkinsSubmittedToday = scopedStandupsToday.length
    const checkinsSubmittedYesterday = scopedStandupsYesterday.length
    const agentsReportedToday = new Set(scopedCheckinsToday.map((c) => c.agent_id)).size
    const agentsReportedYesterday = new Set(scopedCheckinsYesterday.map((c) => c.agent_id)).size
    const blockersToday = scopedStandupsToday.filter((s) => hasBlocker(s.questions)).length
    const blockersYesterday = scopedStandupsYesterday.filter((s) => hasBlocker(s.questions)).length

    const checkinsDelta = countDelta(checkinsSubmittedToday, checkinsSubmittedYesterday)
    const agentsDelta = countDelta(agentsReportedToday, agentsReportedYesterday)
    const blockersDelta = countDelta(blockersToday, blockersYesterday)

    const cards: StatCard[] = [
      {
        label: 'Check-ins submitted',
        icon: <ClipboardList className="h-4 w-4" />,
        value: `${checkinsSubmittedToday}`,
        trendLabel: checkinsDelta.label,
        trendDirection: checkinsDelta.direction,
        upIsGood: true,
      },
      {
        label: 'Connectors reported',
        icon: <Bot className="h-4 w-4" />,
        value: `${agentsReportedToday}`,
        trendLabel: agentsDelta.label,
        trendDirection: agentsDelta.direction,
        upIsGood: true,
      },
      {
        label: 'Blockers detected',
        icon: <AlertTriangle className="h-4 w-4" />,
        value: `${blockersToday}`,
        trendLabel: blockersDelta.label,
        trendDirection: blockersDelta.direction,
        upIsGood: false,
      },
    ]

    // ---- per-pod cards ----
    const relevantPods = selectedPodId === 'all' ? pods : pods.filter((p) => p.id === selectedPodId)

    const cardsData: PodCardData[] = relevantPods.map((pod) => {
      const podMembers = membersByPod.get(pod.id) ?? []
      const podAgents = agents.filter((a) => membersById.get(a.user_id)?.pod_id === pod.id)

      const people = podMembers
        .map((m) => {
          const standup = standupByUserId.get(m.id)
          if (!standup) return null
          return {
            id: m.id,
            name: displayName(m),
            avatarUrl: m.avatar_url,
            time: new Date(standup.created_at).toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
            }),
            update: standup.questions?.today || 'No update provided',
            _sortAt: new Date(standup.created_at).getTime(),
          }
        })
        .filter((p): p is NonNullable<typeof p> => p !== null)
        .sort((a, b) => a._sortAt - b._sortAt)

      const blockersForPod = people.filter((p) => {
        const standup = standupByUserId.get(p.id)
        return hasBlocker(standup?.questions)
      }).length

      const agentEntries = podAgents
        .map((a) => {
          const checkin = earliestCheckin(checkinsByAgentId.get(a.id))
          if (!checkin) return null
          return {
            id: a.id,
            name: a.client_name ?? a.name,
            time: new Date(checkin.created_at).toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
            }),
            update: displayWorkSummary(checkin.task),
            _sortAt: new Date(checkin.created_at).getTime(),
          }
        })
        .filter((a): a is NonNullable<typeof a> => a !== null)
        .sort((a, b) => a._sortAt - b._sortAt)

      return {
        pod,
        peopleCount: people.length,
        people: people.slice(0, 3),
        agentsCount: agentEntries.length,
        agents: agentEntries.slice(0, 3),
        summaryBullets: [
          `${people.length} check-in${people.length === 1 ? '' : 's'}`,
          `${agentEntries.length} connector${agentEntries.length === 1 ? '' : 's'} reported`,
          `${blockersForPod} blocker${blockersForPod === 1 ? '' : 's'} detected`,
        ],
      }
    })

    return { statCards: cards, podCards: cardsData, scopedStandupsToday }
  }, [members, pods, agents, standupsToday, standupsYesterday, checkinsToday, checkinsYesterday, selectedPodId])

  const sentimentValuesToday = scopedStandupsToday
    .map((s) => s.sentiment)
    .filter((v): v is number => v !== null && v !== undefined)
  const avgSentimentToday =
    sentimentValuesToday.length > 0
      ? sentimentValuesToday.reduce((a, b) => a + b, 0) / sentimentValuesToday.length
      : null
  const sentiment = sentimentLabel(avgSentimentToday)

  // Prefer a digest row for this exact pod; fall back to the team-wide row
  // (pod_id null), which is the only kind that exists today. Resolving this
  // per pod (instead of one shared value for every card) is what makes it
  // safe for a future per-pod digest to coexist with today's team-wide one.
  const getDeliveredLabel = (podId: string): string | null => {
    const log = digestLogs.find((d) => d.pod_id === podId) ?? digestLogs.find((d) => d.pod_id === null)
    if (!log?.posted_at) return null
    return `Delivered ${new Date(log.posted_at).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    })}`
  }

  const selectedDateObj = new Date(selectedDate + 'T00:00:00')

  return (
    <SidebarProvider>
      <AppSidebar
        activeSection="standups-summary"
        onNavigate={goToDashboard}
        onOpenTeamManagement={() => navigate(`/dashboard/team/${teamId}/team-management`)}
        onOpenBilling={() => navigate(`/dashboard/team/${teamId}?open=billing`)}
        teamId={teamId}
      />
      <SidebarInset className="min-w-0">
        <TopBar teamId={teamId} teamName={teamName} onLogout={handleLogout} sectionLabel="Team Summary" />
        <main className="min-w-0 space-y-6 bg-background p-4 sm:p-6">
          <div className="flex min-w-0 flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="min-w-0 flex-1">
              <h1 className="mb-1 break-words text-2xl font-semibold text-foreground">
                {teamName ? `${teamName} daily summary` : 'Team daily summary'}
              </h1>
              <p className="text-muted-foreground text-sm">
                Summary of everything that happened across your team and connectors.
              </p>
            </div>
            <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
              <Select value={selectedPodId} onValueChange={setSelectedPodId}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder={isManager ? 'My projects' : 'Company (All teams)'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isManager ? 'My projects' : 'Company (All teams)'}</SelectItem>
                  {pods.map((pod) => (
                    <SelectItem key={pod.id} value={pod.id}>
                      {pod.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DatePicker
                date={selectedDateObj}
                onDateChange={(date) => date && setSelectedDate(toDateString(date))}
                className="w-full sm:w-56 bg-card border border-border"
                placeholder="Select date"
              />
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          {!error && (
            <>
              {/* One seamless bordered block for the whole page body -- KPI strip,
                  "Team Summaries" header, and every pod row all share the same
                  bg-background fill and are separated only by hairline dividers
                  (docs/UI_STYLE_GUIDE.md §2/§3), matching the Figma reference.
                  Previously the KPI cells and each pod row were separate
                  bg-card Cards with gaps/shadows between them -- that reads as
                  a stack of floating white boxes instead of one panel. */}
              <div className="divide-y divide-border overflow-hidden border border-border bg-background shadow-sm">
                <div className="grid grid-cols-1 divide-y divide-border sm:grid-cols-2 sm:divide-x sm:divide-y-0 lg:grid-cols-4">
                  {statCards.map((stat) => (
                    <div key={stat.label} className="min-w-0 space-y-2 p-4 sm:p-5">
                      <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {stat.icon}
                        <span>{stat.label}</span>
                      </div>
                      <div className="text-2xl font-semibold text-foreground sm:text-3xl">
                        {loading ? <Skeleton className="h-7 w-12" /> : stat.value}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        {loading ? (
                          <Skeleton className="h-3 w-28" />
                        ) : (
                          <>
                            <TrendIndicator direction={stat.trendDirection} upIsGood={stat.upIsGood} />
                            <span
                              className={
                                stat.trendDirection === 'flat' ||
                                (stat.trendDirection === 'up') === stat.upIsGood
                                  ? 'font-medium text-success'
                                  : 'font-medium text-destructive'
                              }
                            >
                              {stat.trendLabel}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="min-w-0 space-y-2 p-4 sm:p-5">
                    <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      <span>😊</span>
                      <span>Team sentiment</span>
                    </div>
                    <div className={`text-2xl font-semibold sm:text-3xl ${sentiment.className}`}>
                      {loading ? <Skeleton className="h-7 w-24" /> : `${sentiment.emoji} ${sentiment.label}`}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {loading ? (
                        <Skeleton className="h-3 w-36" />
                      ) : sentimentValuesToday.length > 0 ? (
                        `Based on ${sentimentValuesToday.length} check-in${sentimentValuesToday.length === 1 ? '' : 's'} today`
                      ) : (
                        'No check-ins with sentiment yet today'
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-4 sm:p-6">
                  <h2 className="text-lg font-semibold text-foreground">Team Summaries</h2>
                  <p className="text-sm text-muted-foreground">
                    Click on a team to expand and view more details
                  </p>
                </div>

                {loading ? (
                  // Shape mirrors a collapsed pod row (square avatar, name +
                  // description lines, delivered-badge pill) so the shimmer
                  // reads as "pod rows incoming" rather than a generic bar.
                  <div className="divide-y divide-border">
                    {[0, 1].map((i) => (
                      <div key={i} className="flex items-center justify-between gap-3 p-4 sm:p-5">
                        <div className="flex min-w-0 items-center gap-3">
                          <Skeleton className="h-10 w-10 shrink-0 rounded-md" />
                          <div className="space-y-2">
                            <Skeleton className="h-4 w-28" />
                            <Skeleton className="h-3 w-44" />
                          </div>
                        </div>
                        <Skeleton className="h-6 w-28 shrink-0 rounded-full" />
                      </div>
                    ))}
                  </div>
                ) : podCards.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground sm:p-6">No teams to show.</p>
                ) : (
                  podCards.map((data, index) => (
                    <TeamSummaryCard
                      key={data.pod.id}
                      data={data}
                      deliveredLabel={getDeliveredLabel(data.pod.id)}
                      defaultExpanded={index === 0}
                      onViewCheckins={() =>
                        navigate(
                          `/dashboard/team/${teamId}/standups/check-ins?pod=${encodeURIComponent(data.pod.id)}&date=${selectedDate}`
                        )
                      }
                      onViewAgents={() => navigate(`/dashboard/team/${teamId}/connectors?view=activity&pod=${encodeURIComponent(data.pod.id)}&from=${selectedDate}&to=${selectedDate}`)}
                    />
                  ))
                )}
              </div>
            </>
          )}
        </main>
      </SidebarInset>
    </SidebarProvider>
  )
}

export default TeamStandupSummary
